import { apiFetch } from '@/lib/api'

export type NodeType =
  | 'trigger'
  | 'agent'
  | 'action'
  | 'condition'
  | 'approval'
  | 'wait'
  | 'notify'
  | 'http'
  | 'set'
  | 'loop'
  | 'classify'

export interface NodeData {
  label?: string
  // trigger
  kind?: 'manual' | 'schedule' | 'event' | 'webhook'
  every?: '15m' | 'hourly' | 'daily' | 'weekly'
  at_time?: string
  weekday?: number | null
  event?: string
  // agent / action
  agent?: string
  instructions?: string
  tool?: string
  args?: Record<string, string | number | boolean>
  // condition (rule | ai), loop (map | filter) and classify (smart | thorough)
  mode?: 'rule' | 'ai' | 'map' | 'filter' | 'smart' | 'thorough'
  value?: string
  op?: string
  compare?: string
  question?: string
  // approval
  title?: string
  message?: string
  // wait
  amount?: number
  unit?: 'minutes' | 'hours' | 'days'
  // notify
  email_owner?: boolean
  // http
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url?: string
  headers?: Record<string, string>
  body?: string
  // set
  fields?: Record<string, string | number | boolean>
  // loop
  items?: string
  // classify
  source?: 'new_posts' | 'items'
  target?: string
  not_target?: string
  keywords?: string
  examples?: string
  // any step that can fail
  retries?: number
  on_error?: 'stop' | 'continue'
  [key: string]: unknown
}

export interface GraphNode {
  id: string
  type: NodeType
  position?: { x: number; y: number }
  data: NodeData
}

export interface GraphEdge {
  id?: string
  source: string
  target: string
  sourceHandle?: 'yes' | 'no' | 'error' | null
}

export interface Graph {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

export type RunStatus = 'running' | 'waiting' | 'done' | 'failed' | 'stopped'
export type StepStatus =
  | 'running'
  | 'waiting'
  | 'resumed'
  | 'done'
  | 'failed'
  | 'skipped'

export interface WorkflowSummary {
  id: number
  name: string
  description: string
  enabled: boolean
  build_status: 'idle' | 'building' | 'asking' | 'failed'
  trigger: string
  trigger_kind: string
  steps: number
  agents: string[]
  runs_count: number
  last_run_at: string | null
  last_run_status: RunStatus | null
  next_run_at: string | null
  updated_at: string
  created_at: string
}

export interface BuildLine {
  at: string
  agent: string
  text: string
  node?: string | null
  kind?: string
}

/** What the team is doing on the canvas right now, while it builds. */
export interface BuildFocus {
  node?: string | null
  agent?: string
  kind?: 'add' | 'change' | 'connect' | 'remove' | 'trigger' | 'thinking'
  text?: string
  edge?: { source: string; target: string } | null
  at?: string
}

export interface WorkflowRunStep {
  id: number
  node_id: string
  type: NodeType
  label: string
  status: StepStatus
  status_label: string
  output: string
  branch: string
  error: string
  attempts: number
  started_at: string
  finished_at: string | null
  resume_at: string | null
  card: { id: number; title: string; status: string } | null
}

export interface WorkflowRun {
  id: number
  workflow: number
  status: RunStatus
  status_label: string
  trigger_text: string
  summary: string
  error: string
  started_at: string
  finished_at: string | null
  started_by: string
  steps?: WorkflowRunStep[]
}

export interface WorkflowDetail extends WorkflowSummary {
  prompt: string
  graph: Graph
  build_log: BuildLine[]
  build_focus: BuildFocus
  build_error: string
  errors: string[]
  /** For webhook triggers: the path other apps POST to (prefix with the API address). */
  hook_path: string
  /** Chief of Staff's multiple-choice questions while the build waits on them. */
  questions: BuildQuestion[]
  /** Per step: the owner's right/wrong counts and what the last check did. */
  memory: Record<string, StepMemory>
  runs: WorkflowRun[]
}

export interface BuildQuestion {
  id: string
  question: string
  options: Array<{ label: string; hint: string }>
  multi: boolean
}

export interface ClassifyStats {
  checked: number
  by_keyword: number
  by_meaning: number
  to_ai: number
  second_opinion: number
  matched: number
  cost_usd: number
  at: string
  meaning_skipped?: boolean
  bad_keywords?: string[]
}

export interface StepMemory {
  stats: { right?: number; wrong?: number }
  last: Partial<ClassifyStats>
}

/** One post a classify step found, as shown in the chat. */
export interface ClassifyMatch {
  id: number | string
  title: string
  text: string
  why: string
  confidence: number
  link: string
  found_by: string
  verdict?: boolean
}

export interface CatalogAgent {
  key: string
  name: string
  duty: string
  enabled: boolean
}

export interface CatalogAction {
  name: string
  label: string
  description: string
  money: boolean
  agents: string[]
  params: Array<{
    name: string
    description: string
    required: boolean
    enum: string[]
  }>
}

export interface Catalog {
  agents: CatalogAgent[]
  actions: CatalogAction[]
  events: Array<{ key: string; label: string }>
  ops: Array<{ key: string; label: string }>
}

const BASE = '/api/admin/ai-team'

export const fetchWorkflows = () =>
  apiFetch<{ workflows: WorkflowSummary[] }>(`${BASE}/workflows/`)

export const fetchWorkflow = (id: number) =>
  apiFetch<WorkflowDetail>(`${BASE}/workflows/${id}/`)

export const fetchCatalog = () => apiFetch<Catalog>(`${BASE}/workflows/catalog/`)

/** Ask Chief of Staff to build a new workflow, or (with `workflow`) to change one.
 * With `room`, its card is posted in that chat. */
export const buildWorkflow = (prompt: string, workflow?: number, room?: number) =>
  apiFetch<WorkflowDetail>(`${BASE}/workflows/build/`, {
    method: 'POST',
    body: { prompt, ...(workflow ? { workflow } : {}), ...(room ? { room } : {}) }
  })

export const createWorkflow = (name = 'New workflow') =>
  apiFetch<WorkflowDetail>(`${BASE}/workflows/`, {
    method: 'POST',
    body: { name }
  })

export const saveWorkflow = (
  id: number,
  body: Partial<{
    name: string
    description: string
    graph: Graph
    enabled: boolean
    /** Lay every step out again, neatly. */
    tidy: boolean
  }>
) =>
  apiFetch<WorkflowDetail>(`${BASE}/workflows/${id}/`, {
    method: 'PUT',
    body
  })

export const deleteWorkflow = (id: number) =>
  apiFetch(`${BASE}/workflows/${id}/`, { method: 'DELETE' })

export const stopBuild = (id: number) =>
  apiFetch<WorkflowDetail>(`${BASE}/workflows/${id}/stop-build/`, { method: 'POST' })

export const answerWorkflow = (id: number, answers: Record<string, string[]>) =>
  apiFetch<WorkflowDetail>(`${BASE}/workflows/${id}/answer/`, {
    method: 'POST',
    body: { answers }
  })

export const sendFeedback = (
  id: number,
  body: { node: string; item: { id: number | string; text: string }; match: boolean; message?: number }
) =>
  apiFetch<{ stats: { right?: number; wrong?: number } }>(`${BASE}/workflows/${id}/feedback/`, {
    method: 'POST',
    body
  })

export interface TemplateConnector {
  key: string
  name: string
  use: string
  ready: boolean
  setup?: string
}

export interface TemplateCard {
  key: string
  name: string
  category: string
  summary: string
  how: string[]
  trigger: string
  starter: boolean
  note: string
  steps: number
  agents: Array<{ key: string; name: string }>
  connectors: TemplateConnector[]
  questions: Array<{ id: string; question: string; options: Array<{ label: string; hint: string }> }>
}

export const fetchTemplates = () =>
  apiFetch<{ templates: TemplateCard[]; categories: string[] }>(`${BASE}/workflows/templates/`)

/** Add a workflow from a template, with the owner's answers; `room` posts its card in that chat. */
export const addFromTemplate = (
  key: string,
  body: { answers: Record<string, string>; room?: number; enable?: boolean }
) =>
  apiFetch<WorkflowDetail>(`${BASE}/workflows/templates/${key}/use/`, { method: 'POST', body })

export const runWorkflow = (id: number) =>
  apiFetch<WorkflowRun>(`${BASE}/workflows/${id}/run/`, { method: 'POST' })

export const fetchRun = (id: number) =>
  apiFetch<WorkflowRun>(`${BASE}/workflow-runs/${id}/`)

export const cancelRun = (id: number) =>
  apiFetch<WorkflowRun>(`${BASE}/workflow-runs/${id}/cancel/`, {
    method: 'POST'
  })

export const RUN_ACTIVE: RunStatus[] = ['running', 'waiting']

export const NODE_TYPES: Array<{
  type: Exclude<NodeType, 'trigger'>
  label: string
  hint: string
}> = [
  { type: 'agent', label: 'Agent', hint: 'A teammate looks into it or writes something' },
  { type: 'action', label: 'Action', hint: 'One concrete change, asks you first' },
  { type: 'condition', label: 'Check', hint: 'Yes / no, then branch' },
  { type: 'approval', label: 'Your approval', hint: 'You decide before it goes on' },
  { type: 'wait', label: 'Wait', hint: 'Pause for minutes, hours or days' },
  { type: 'notify', label: 'Message', hint: 'Post in the team room or email you' },
  { type: 'http', label: 'Web call', hint: 'Call another app and read its answer' },
  { type: 'set', label: 'Record', hint: 'Gather values into named fields' },
  { type: 'loop', label: 'For each', hint: 'Go through a list, item by item' },
  { type: 'classify', label: 'Smart check', hint: 'Find posts or items that match, cheaply' }
]

/** Steps that can fail on their own: they retry and can have an error path. */
export const FALLIBLE: NodeType[] = [
  'agent',
  'action',
  'condition',
  'notify',
  'http',
  'set',
  'loop',
  'classify'
]

export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]
